/**
 * Utility functions for office hours validation
 * Shared between frontend and backend for consistent business logic
 */
export class OfficeHoursValidator {
    constructor() {
        // Interface compliance (instance methods delegate to static methods)
        this.hasAtLeastOneEnabledDay = OfficeHoursValidator.hasAtLeastOneEnabledDay;
        this.enabledDaysHaveTimeRanges = OfficeHoursValidator.enabledDaysHaveTimeRanges;
        this.validateMaxTimeRangesPerDay = OfficeHoursValidator.validateMaxTimeRangesPerDay;
        this.hasTimeRangeOverlap = OfficeHoursValidator.hasTimeRangeOverlap;
    }
    /**
     * Validates that at least one day is enabled
     */
    static hasAtLeastOneEnabledDay(schedule) {
        const days = [
            'monday',
            'tuesday',
            'wednesday',
            'thursday',
            'friday',
            'saturday',
            'sunday',
        ];
        return days.some(day => schedule[day]?.enabled === true);
    }
    /**
     * Validates that enabled days have at least one time range
     */
    static enabledDaysHaveTimeRanges(schedule) {
        const days = [
            'monday',
            'tuesday',
            'wednesday',
            'thursday',
            'friday',
            'saturday',
            'sunday',
        ];
        for (const day of days) {
            const daySchedule = schedule[day];
            if (daySchedule?.enabled &&
                (!daySchedule.timeRanges || daySchedule.timeRanges.length === 0)) {
                return false;
            }
        }
        return true;
    }
    /**
     * Validates maximum time ranges per day (currently 2)
     */
    static validateMaxTimeRangesPerDay(schedule) {
        const days = [
            'monday',
            'tuesday',
            'wednesday',
            'thursday',
            'friday',
            'saturday',
            'sunday',
        ];
        for (const day of days) {
            const daySchedule = schedule[day];
            if (daySchedule?.timeRanges && daySchedule.timeRanges.length > 2) {
                return false;
            }
        }
        return true;
    }
    /**
     * Checks if time ranges overlap within a day
     */
    static hasTimeRangeOverlap(timeRanges) {
        if (timeRanges.length <= 1)
            return false;
        for (let i = 0; i < timeRanges.length; i++) {
            for (let j = i + 1; j < timeRanges.length; j++) {
                const range1 = timeRanges[i];
                const range2 = timeRanges[j];
                const start1 = this.timeToMinutes(range1.from);
                const end1 = this.timeToMinutes(range1.to);
                const start2 = this.timeToMinutes(range2.from);
                const end2 = this.timeToMinutes(range2.to);
                // Check for overlap: start1 < end2 && start2 < end1
                if (start1 < end2 && start2 < end1) {
                    return true;
                }
            }
        }
        return false;
    }
    /**
     * Validates a single time range (start time before end time)
     */
    static isValidTimeRange(range) {
        const fromMinutes = this.timeToMinutes(range.from);
        const toMinutes = this.timeToMinutes(range.to);
        return fromMinutes < toMinutes;
    }
    /**
     * Validates time format (HH:MM)
     */
    static isValidTimeFormat(time) {
        return /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(time);
    }
    /**
     * Converts time string to minutes for comparison
     */
    static timeToMinutes(time) {
        const [hours, minutes] = time.split(':').map(Number);
        return hours * 60 + minutes;
    }
}
/**
 * Utility functions for transforming between frontend and TwentyCRM formats
 * Shared between frontend and backend for consistent data transformation
 * // TODO: This is a temporary solution to transform the data between the frontend and backend.
 * // We may be able to remove
 */
export class OfficeHoursTransformer {
    constructor() {
        // Interface compliance (instance methods delegate to static methods)
        this.toTwentyCRMFormat = OfficeHoursTransformer.toTwentyCRMFormat;
        this.fromTwentyCRMFormat = OfficeHoursTransformer.fromTwentyCRMFormat;
    }
    /**
     * Transforms frontend schedule format to TwentyCRM format
     */
    static toTwentyCRMFormat(frontendSchedule) {
        const days = [
            'monday',
            'tuesday',
            'wednesday',
            'thursday',
            'friday',
            'saturday',
            'sunday',
        ];
        const twentySchedule = {};
        for (const day of days) {
            const daySchedule = frontendSchedule[day];
            if (!daySchedule ||
                !daySchedule.enabled ||
                !daySchedule.timeRanges ||
                daySchedule.timeRanges.length === 0) {
                // Day is disabled or no time ranges
                twentySchedule[day] = [{ allDay: false }];
            }
            else {
                // Transform each time range to TwentyCRM format
                twentySchedule[day] = daySchedule.timeRanges.map((range) => ({
                    allDay: false,
                    startTime: range.from,
                    endTime: range.to,
                }));
            }
        }
        return twentySchedule;
    }
    /**
     * Transforms TwentyCRM format to frontend format
     */
    static fromTwentyCRMFormat(twentySchedule) {
        const days = [
            'monday',
            'tuesday',
            'wednesday',
            'thursday',
            'friday',
            'saturday',
            'sunday',
        ];
        const frontendSchedule = {};
        for (const day of days) {
            const daySchedule = twentySchedule[day];
            if (!daySchedule ||
                daySchedule.length === 0 ||
                (daySchedule.length === 1 &&
                    daySchedule[0].allDay === false &&
                    !daySchedule[0].startTime)) {
                // Day is disabled
                frontendSchedule[day] = {
                    enabled: false,
                    timeRanges: [{ from: '09:00', to: '17:00' }], // Default range
                };
            }
            else {
                // Transform TwentyCRM ranges to frontend format
                const timeRanges = daySchedule
                    .filter(range => range.startTime && range.endTime)
                    .map(range => ({
                    from: range.startTime,
                    to: range.endTime,
                }));
                frontendSchedule[day] = {
                    enabled: timeRanges.length > 0,
                    timeRanges: timeRanges.length > 0
                        ? timeRanges
                        : [{ from: '09:00', to: '17:00' }],
                };
            }
        }
        return frontendSchedule;
    }
}
/**
 * Default timezone for business hours
 */
export const DEFAULT_TIMEZONE = 'AMERICA_NEW_YORK';
/**
 * Default weekly schedule for business hours
 */
export const DEFAULT_WEEKLY_SCHEDULE = {
    monday: { enabled: true, timeRanges: [{ from: '09:00', to: '17:00' }] },
    tuesday: { enabled: true, timeRanges: [{ from: '09:00', to: '17:00' }] },
    wednesday: { enabled: true, timeRanges: [{ from: '09:00', to: '17:00' }] },
    thursday: { enabled: true, timeRanges: [{ from: '09:00', to: '17:00' }] },
    friday: { enabled: true, timeRanges: [{ from: '09:00', to: '17:00' }] },
    saturday: { enabled: false, timeRanges: [{ from: '09:00', to: '17:00' }] },
    sunday: { enabled: false, timeRanges: [{ from: '09:00', to: '17:00' }] },
};
//# sourceMappingURL=office-hours.js.map